import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/api/firebase/firebase";
import { logger } from "@/services/logger";

// A single answer captured from a dynamic (template-driven) feedback form.
export interface FeedbackAnswer {
  questionId: string;
  question: string;
  type: string;
  answer: string;
}

export interface SessionFeedback {
  id?: string;
  cohortId: string;
  trainerId: string;
  trainerName: string;
  whoTrained: string;
  date: string;
  todayWas: string;
  // Dynamic, template-driven feedback. When a cohort has a linked feedback
  // template, the form is rendered from it and answers are stored here.
  templateId?: string;
  templateName?: string;
  answers?: FeedbackAnswer[];
  // Legacy fixed-form fields — optional so template-driven submissions are
  // valid without them.
  attendanceCount?: string;
  delegatesOnTime?: 'yes' | 'no' | '';
  lateStartTime?: string;
  camerasOffCount?: string;
  camerasOffReason?: string;
  homeworkSubmittedCount?: string;
  notFollowingUp?: string;
  specificIssues?: string;
  coverageDetails?: string;
  sessionFeeling?: string;
  participationRating?: string;
  briefComments?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export class FeedbackService {
  private static readonly COLLECTION_NAME = "trainerFeedback";

  /**
   * Submit new trainer feedback
   */
  static async submitFeedback(
    feedbackData: Omit<SessionFeedback, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const newFeedback = {
        ...feedbackData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(
        collection(db, this.COLLECTION_NAME),
        newFeedback
      );

      logger.info('Trainer feedback submitted successfully', {
        feedbackId: docRef.id,
        cohortId: feedbackData.cohortId
      }, 'FEEDBACK_SERVICE');

      return docRef.id;
    } catch (error) {
      logger.error('Error submitting trainer feedback', error, 'FEEDBACK_SERVICE');
      throw new Error("Failed to submit feedback");
    }
  }

  /**
   * Get all feedback for a specific cohort
   */
  static async getFeedbackByCohort(cohortId: string): Promise<SessionFeedback[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where("cohortId", "==", cohortId)
      );

      const querySnapshot = await getDocs(q);
      const feedback: SessionFeedback[] = [];

      querySnapshot.forEach((doc) => {
        feedback.push({
          id: doc.id,
          ...doc.data(),
        } as SessionFeedback);
      });

      // Sort by createdAt descending in memory
      feedback.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      });

      logger.info('Loaded feedback for cohort', {
        cohortId,
        count: feedback.length
      }, 'FEEDBACK_SERVICE');

      return feedback;
    } catch (error) {
      logger.error('Error loading cohort feedback', error, 'FEEDBACK_SERVICE');
      throw new Error("Failed to load feedback");
    }
  }

  /**
   * Get all feedback submitted by a specific trainer
   */
  static async getFeedbackByTrainer(trainerId: string): Promise<SessionFeedback[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where("trainerId", "==", trainerId)
      );

      const querySnapshot = await getDocs(q);
      const feedback: SessionFeedback[] = [];

      querySnapshot.forEach((doc) => {
        feedback.push({
          id: doc.id,
          ...doc.data(),
        } as SessionFeedback);
      });

      // Sort by createdAt descending in memory
      feedback.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      });

      logger.info('Loaded feedback for trainer', {
        trainerId,
        count: feedback.length
      }, 'FEEDBACK_SERVICE');

      return feedback;
    } catch (error) {
      logger.error('Error loading trainer feedback', error, 'FEEDBACK_SERVICE');
      throw new Error("Failed to load trainer feedback");
    }
  }

  /**
   * Get all feedback across all cohorts (admin view)
   */
  static async getAllFeedback(): Promise<SessionFeedback[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const feedback: SessionFeedback[] = [];

      querySnapshot.forEach((doc) => {
        feedback.push({
          id: doc.id,
          ...doc.data(),
        } as SessionFeedback);
      });

      logger.info('Loaded all feedback', {
        count: feedback.length
      }, 'FEEDBACK_SERVICE');

      return feedback;
    } catch (error) {
      logger.error('Error loading all feedback', error, 'FEEDBACK_SERVICE');
      throw new Error("Failed to load all feedback");
    }
  }
}
